import { useEffect, useState } from "react";
import uuid from "uuid";
import { useLocalStorageState } from "../../../../../hooks/useLocalStorageState";
import {
  INTEGRATION_STATE_STORAGE_KEY,
  INTEGRATION_SYNC_ALL_KEY,
  INTEGRATION_SYNC_NEW_KEY,
  REFETCH_INTERVAL_MS,
  UNKNOWN_AUTHORIZATION_DATE,
} from "../config";
import { useConstantContactAuthDataQuery } from "../../../../../api/constantContact/useConstantContactAuthDataQuery";
import { uiNotification } from "../../../../../services/UINotificationService";
import { useSaveConstantContactAuthDataMutation } from "../../../../../api/constantContact/useSaveConstantContactAuthDataMutation";

const parseBoolean = (value) => {
  return value === "true";
};

export function useIntegration() {
  const [isEnabled, setIsEnabled] = useState(false);
  const [url, setUrl] = useState("");
  const [date, setDate] = useState(null);
  const [enableRefetchInterval, setEnableRefetchInterval] = useState(false);
  const [windowRef, setWindowRef] = useState(null);

  const { mutateAsync: saveIntegrationData } =
    useSaveConstantContactAuthDataMutation({
      onError: (error) => {
        const message =
          error?.response?.data?.message ||
          "Unable to save integration data. Try again later.";
        uiNotification.error(message);
      },
    });

  const [syncAll, setSyncAll] = useLocalStorageState(
    INTEGRATION_SYNC_ALL_KEY,
    false,
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const [syncNew, setSyncNew] = useLocalStorageState(
    INTEGRATION_SYNC_NEW_KEY,
    false,
    {
      transform: parseBoolean,
      enableInit: true,
    },
  );

  const { isLoading } = useConstantContactAuthDataQuery({
    refetchInterval: enableRefetchInterval ? REFETCH_INTERVAL_MS : false,
    refetchIntervalInBackground: enableRefetchInterval,
    onError: () => {
      uiNotification.error(
        "Unable to fetch integration data. Try again later.",
      );
    },
    onSuccess: (data) => {
      const enabled = data.isAuth;
      if (!enabled && !enableRefetchInterval) {
        setIsEnabled(false);
      }
      if (enabled) {
        setIsEnabled(true);
        setEnableRefetchInterval(false);
      }
      setUrl(data.url);
      setDate(enabled ? data.date || UNKNOWN_AUTHORIZATION_DATE : null);
      setSyncAll(data.syncAll);
      setSyncNew(data.syncNew);
    },
  });

  const onAuthorize = () => {
    const state = uuid.v4();
    sessionStorage.setItem(INTEGRATION_STATE_STORAGE_KEY, state);

    const separator = url.includes("?") ? "&" : "?";
    const urlWithState = `${url}${separator}state=${state}`;

    const newWindow = window.open(
      urlWithState,
      "Ratting",
      "width=550,height=550,left=150,top=200,toolbar=0,status=0,scrollbars=1",
    );
    /**
     * The opened window (which navigates to a third-party Constant Contact
     * OAuth URL) retains a reference to window.opener, giving it (or any page
     * it navigates to) access to the opener's DOM and localStorage, which
     * contains JWT authentication tokens.
     */
    if (newWindow) {
      newWindow.opener = null;
      setWindowRef(newWindow);
      setEnableRefetchInterval(true);
    }
  };

  useEffect(() => {
    if (!windowRef) {
      return;
    }

    const interval = setInterval(() => {
      if (windowRef.closed) {
        setEnableRefetchInterval(false);
        setWindowRef(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [windowRef]);

  const handleIsEnabled = (next) => {
    const next_ = typeof next === "function" ? next(isEnabled) : next;
    if (next_) {
      setIsEnabled(true);
    } else {
      setIsEnabled(false);
      setEnableRefetchInterval(false);
      saveIntegrationData({
        code: null,
        isEnabled: false,
        syncAll: false,
        syncNew: false,
      }).catch(() => {
        setIsEnabled(true);
      });
    }
  };

  useEffect(() => {
    if (isEnabled && !isLoading && !enableRefetchInterval) {
      saveIntegrationData({
        code: null,
        isEnabled: true,
        syncAll,
        syncNew,
      });
    }
  }, [syncAll, syncNew]);

  useEffect(() => {
    if (!isEnabled) {
      setEnableRefetchInterval(false);
    }
  }, [isEnabled]);

  return {
    isEnabled,
    setIsEnabled: handleIsEnabled,
    syncAll,
    setSyncAll,
    syncNew,
    setSyncNew,
    isLoading,
    date,
    onAuthorize,
  };
}
